import {
  Box,
  Drawer,
  List,
  ListItemButton,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScrollBar from "simplebar-react";
import topMenuList, { MenuItem } from "./topMenuList";

interface SideNavBarProps {
  showMobileSideBar: boolean;
  closeMobileSideBar: () => void;
}

const MainMenu = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  left: 0,
  width: expanded ? 240 : 88,
  height: "100%",
  position: "fixed",
  boxShadow: theme.shadows[2],
  transition: "width 0.25s ease",
  zIndex: theme.zIndex.drawer + 11,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: { left: -88 },
  "& .simplebar-track.simplebar-vertical": { width: 6 },
  "& .simplebar-scrollbar:before": {
    background: theme.palette.text.secondary,
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  marginBottom: "0.4rem",
  justifyContent: expanded ? "flex-start" : "center",
  paddingLeft: expanded ? "0.9rem" : "0.5rem",
  marginInline: "0.5rem",
  borderRadius: 12,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DashboardSideBar: FC<SideNavBarProps> = ({
  showMobileSideBar,
  closeMobileSideBar,
}) => {
  const navigate = useNavigate();
  const [active, setActive] = useState("Dashboard");
  const [expanded, setExpanded] = useState(false);
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const handleActiveMainMenu = (menuItem: MenuItem) => () => {
    setActive(menuItem.title);
    navigate(menuItem.path);
    closeMobileSideBar();
  };

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const mainSideBarContent = (
    <List sx={{ height: "100%", p: 1 }}>
      <StyledListItemButton
        disableRipple
        expanded={expanded}
        onClick={toggleExpanded}
      >
        <img src="/static/logo/logo.svg" alt="Logo" width={74} />
        {expanded ? (
          <Typography variant="h6" sx={{ ml: 1 }}></Typography>
        ) : null}
      </StyledListItemButton>

      <ScrollBar style={{ maxHeight: "calc(100% - 56px)" }}>
        {topMenuList.map((nav, index) => (
          <Tooltip
            title={!expanded ? nav.title : ""}
            placement="right"
            key={index}
          >
            <StyledListItemButton
              disableRipple
              onClick={handleActiveMainMenu(nav)}
              expanded={expanded}
              sx={{
                backgroundColor:
                  active === nav.title ? "action.hover" : "transparent",
              }}
            >
              <nav.Icon
                sx={{
                  color:
                    active === nav.title ? "primary.main" : "text.secondary",
                }}
              />
              {expanded ? (
                <Typography sx={{ ml: 1 }}>{nav.title}</Typography>
              ) : null}
            </StyledListItemButton>
          </Tooltip>
        ))}
      </ScrollBar>
    </List>
  );

  if (downMd) {
    return (
      <Drawer
        anchor="left"
        open={showMobileSideBar}
        onClose={closeMobileSideBar}
        PaperProps={{ sx: { width: expanded ? 240 : 88 } }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            width: "inherit",
            position: "fixed",
            overflow: "hidden",
            flexDirection: "column",
            boxShadow: (theme) => theme.shadows[1],
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            "& .simplebar-track.simplebar-vertical": { width: 6 },
            "& .simplebar-scrollbar:before": {
              background: (theme) => theme.palette.text.secondary,
            },
          }}
        >
          {mainSideBarContent}
        </Box>
      </Drawer>
    );
  }

  return <MainMenu expanded={expanded}>{mainSideBarContent}</MainMenu>;
};

export default DashboardSideBar;
